
import { useState, useEffect } from "react";
import { useReviews, ReviewWithUserData } from "@/hooks/useReviews";
import { RatingOverview } from "@/components/RatingOverview";
import { RecentReviews } from "@/components/RecentReviews";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface ProviderRatingsProps {
  providerId: string;
  maxReviews?: number;
}

export function ProviderRatings({ providerId, maxReviews = 3 }: ProviderRatingsProps) {
  const { getProviderReviews, getProviderAverageRating, loading } = useReviews();
  const [reviews, setReviews] = useState<ReviewWithUserData[]>([]);
  const [ratingData, setRatingData] = useState({
    average: 0,
    total: 0,
    distribution: { star1: 0, star2: 0, star3: 0, star4: 0, star5: 0 }
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadProviderRatingData() {
      setIsLoading(true);
      try {
        const [reviewsData, ratingData] = await Promise.all([
          getProviderReviews(providerId),
          getProviderAverageRating(providerId)
        ]);
        
        setReviews(reviewsData);
        setRatingData(ratingData);
      } catch (error) {
        console.error("Error loading provider rating data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    if (providerId) {
      loadProviderRatingData();
    }
  }, [providerId, getProviderReviews, getProviderAverageRating]);

  if (isLoading || loading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-1/3" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-40 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Format the reviews for the RecentReviews component
  const formattedReviews = reviews.map(review => ({
    id: review.id,
    rating: review.rating,
    comment: review.comment || "",
    reviewer: {
      id: review.reviewer_id,
      name: review.reviewer.name,
      avatarUrl: undefined, // We don't have avatar URLs in our data
    },
    date: new Date(review.created_at),
    serviceTitle: review.serviceTitle,
    verified: review.is_verified
  }));

  return (
    <div className="space-y-6">
      <RatingOverview
        averageRating={ratingData.average}
        totalReviews={ratingData.total}
        ratingDistribution={{
          star5: ratingData.distribution.star5,
          star4: ratingData.distribution.star4,
          star3: ratingData.distribution.star3,
          star2: ratingData.distribution.star2,
          star1: ratingData.distribution.star1
        }}
      />
      
      {ratingData.total > 0 ? (
        <RecentReviews 
          reviews={formattedReviews}
          maxItems={maxReviews}
          viewAllLink={`/reviews/${providerId}`}
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Recent Reviews</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center text-muted-foreground py-8">
              No reviews yet
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
